import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Upload, Building2, Save, Power, Copy, KeyRound, Search, Lock, Trash2 } from "lucide-react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MoneyInput from "@/components/MoneyInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { maskCNPJ, maskPhone, maskCEP, lookupCEP } from "@/lib/masks";

const EMPTY = {
  name: "", nickname: "", email: "", cnpj: "", address: "", address_number: "", city: "", neighborhood: "", state: "", cep: "", school_type: "",
  phone: "", whatsapp_director: "", director_name: "",
  system_email: "", registration_date: "", collaboration_term: "",
  current_term_code: "", new_term_code: "", active: true,
  photo_path: null,
  fdp_active: false,
  fdp_max_value: 0,
  fdp_period_months: 6,
  fdp_start_date: "",
};

export default function SchoolForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const r = await api.get(`/schools/${id}`);
        setForm({ ...EMPTY, ...r.data });
        origRef.current = `${r.data.name || ""}|${r.data.cnpj || ""}`;
      } catch (e) {
        toast.error("Erro ao carregar escola");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [cepLoading, setCepLoading] = useState(false);
  const handleCepLookup = async (cepValue) => {
    const target = cepValue ?? form.cep;
    const digits = String(target || "").replace(/\D/g, "");
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const data = await lookupCEP(digits);
      if (!data) {
        toast.error("CEP não encontrado");
        return;
      }
      setForm((f) => ({
        ...f,
        address: data.address || f.address,
        neighborhood: data.neighborhood || f.neighborhood,
        city: data.city || f.city,
        state: data.state || f.state,
      }));
      toast.success("Endereço preenchido automaticamente");
    } finally {
      setCepLoading(false);
    }
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update("photo_path", r.data.path);
      toast.success("Foto carregada");
    } catch (err) {
      toast.error("Erro ao enviar foto");
    } finally {
      setUploading(false);
    }
  };

  const origRef = useRef("");
  const [dup, setDup] = useState(null);
  const dupBlocks = !!dup && `${form.name || ""}|${form.cnpj || ""}` !== origRef.current;
  useEffect(() => {
    if ((form.name || "").trim().length < 4 && (form.cnpj || "").replace(/\D/g, "").length < 14) { setDup(null); return undefined; }
    const t = setTimeout(() => {
      api.get("/schools-check-duplicate", { params: { name: form.name || "", cnpj: form.cnpj || "", exclude_id: id || "" }, silent: true })
        .then((r) => setDup(r.data?.duplicate || null)).catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [form.name, form.cnpj, id]);

  const save = async () => {
    if (!form.name?.trim()) {
      toast.error("O nome da escola é obrigatório");
      return;
    }
    if (dupBlocks) {
      toast.error(`Escola já cadastrada: ${dup.name}`);
      return;
    }
    setSaving(true);
    try {
      if (id) {
        await api.put(`/schools/${id}`, form);
        // Also sync FDP settings via its dedicated endpoint
        await api.patch(`/fdp/schools/${id}`, {
          fdp_active: !!form.fdp_active,
          fdp_max_value: Number(form.fdp_max_value) || 0,
          fdp_period_months: Number(form.fdp_period_months) || 0,
          fdp_start_date: form.fdp_start_date || null,
        });
        toast.success("Escola atualizada");
      } else {
        await api.post("/schools", form);
        toast.success("Escola cadastrada");
      }
      nav("/escolas");
    } catch (e) {
      toast.error(e?.response?.status === 409 ? e.response.data.detail : "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    if (!id) return;
    try {
      const r = await api.patch(`/schools/${id}/toggle`);
      update("active", r.data.active);
      toast.success(r.data.active ? "Escola ativada" : "Escola desativada");
    } catch (e) {
      toast.error("Erro");
    }
  };

  const copyInviteLink = async () => {
    const url = `${window.location.origin}/convite/${form.invite_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado!");
    } catch (e) {
      toast.error("Não foi possível copiar");
    }
  };

  const resetAccess = async () => {
    const ok = window.confirm(
      "Resetar o acesso irá remover TODOS os cadastros atuais da escola no portal e gerar um novo link de convite. Deseja continuar?"
    );
    if (!ok) return;
    try {
      const r = await api.post(`/schools/${id}/reset-access`);
      update("invite_token", r.data.invite_token);
      toast.success(`Acesso resetado. ${r.data.deleted_users} usuário(s) removido(s). Compartilhe o novo link.`);
    } catch (e) {
      toast.error("Erro ao resetar acesso");
    }
  };

  const hardDeleteSchool = async () => {
    if (!id) return;
    const typed = window.prompt(
      `ATENÇÃO: esta ação é IRREVERSÍVEL.\n\nEla apaga a escola "${form.name}" e TODOS os seus orçamentos, itens, observações e cadastros de usuários do portal.\n\nPara confirmar, digite EXCLUIR abaixo:`
    );
    if (!typed) return;
    if (typed.trim().toUpperCase() !== "EXCLUIR") {
      toast.error("Confirmação incorreta — nada foi excluído.");
      return;
    }
    try {
      const r = await api.delete(`/schools/${id}`);
      toast.success(
        `Escola excluída. ${r.data.budgets_deleted} orçamento(s) e ${r.data.users_deleted} usuário(s) removidos.`
      );
      nav("/escolas");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir escola");
    }
  };

  if (loading) return <div className="text-slate-500">Carregando...</div>;

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => nav("/escolas")} data-testid="btn-voltar" className="shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 truncate">{id ? "Editar escola" : "Nova escola"}</h1>
            <p className="text-sm text-slate-600 truncate">Preencha as informações da escola</p>
          </div>
        </div>
        {id && (
          <div className="grid grid-cols-2 sm:flex sm:items-center gap-2 shrink-0">
            <Button
              variant="outline"
              onClick={toggleActive}
              data-testid="btn-toggle-active"
              className={`w-full sm:w-auto ${form.active ? "border-red-200 text-red-600 hover:bg-red-50" : "border-green-200 text-green-700 hover:bg-green-50"}`}
            >
              <Power className="w-4 h-4 mr-2" />
              {form.active ? "Desativar" : "Ativar"}
            </Button>
            <Button
              variant="outline"
              onClick={hardDeleteSchool}
              data-testid="btn-hard-delete"
              className="w-full sm:w-auto border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir escola
            </Button>
          </div>
        )}
      </div>

      {id && form.invite_token && (
        <div className="bg-gradient-to-br from-[#1e3a8a] to-[#4c1d95] text-white rounded-2xl p-5 card-shadow">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                <KeyRound className="w-5 h-5" strokeWidth={2.4} />
              </div>
              <div>
                <h3 className="font-heading text-base font-bold">Portal da Escola — Link de convite</h3>
                <p className="text-xs text-white/80 mt-1">
                  Compartilhe este link com o diretor para que ele crie o acesso da escola ao portal.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={resetAccess}
              className="border-white/30 bg-white/10 text-white hover:bg-white/20"
              data-testid="btn-reset-access"
            >
              <KeyRound className="w-4 h-4 mr-2" />
              Resetar acesso
            </Button>
          </div>
          <div className="mt-4 flex items-center gap-2 bg-white/15 rounded-xl p-3 overflow-hidden">
            <code className="flex-1 text-xs md:text-sm truncate font-mono text-white/90" data-testid="invite-link">
              {`${window.location.origin}/convite/${form.invite_token}`}
            </code>
            <Button
              size="sm"
              onClick={copyInviteLink}
              className="bg-white text-[#1e3a8a] hover:bg-white/90 shrink-0"
              data-testid="btn-copy-invite"
            >
              <Copy className="w-3.5 h-3.5 mr-1" />
              Copiar
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-100 card-shadow p-6 space-y-6">
        <div>
          <Label className="text-sm font-medium text-slate-700">Foto da escola</Label>
          <p className="text-xs text-slate-500 mt-1 mb-3">PNG, JPG ou WEBP</p>
          <div className="w-full max-w-xs rounded-xl bg-slate-50 border border-slate-200 border-dashed overflow-hidden aspect-[4/3] flex items-center justify-center">
            {form.photo_path ? (
              <PhotoPreview path={form.photo_path} />
            ) : (
              <Building2 className="w-10 h-10 text-slate-300" />
            )}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 max-w-xs">
            <label
              className="inline-flex items-center justify-center gap-2 h-10 px-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 text-[13px] font-semibold text-slate-700"
              data-testid="btn-upload-photo"
            >
              <Upload className="w-4 h-4" />
              {uploading ? "Enviando..." : form.photo_path ? "Alterar" : "Enviar imagem"}
              <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} disabled={uploading} />
            </label>
            <button
              type="button"
              onClick={() => update("photo_path", null)}
              disabled={!form.photo_path || uploading}
              data-testid="btn-remove-photo"
              className="inline-flex items-center justify-center gap-2 h-10 px-3 border border-red-200 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 text-[13px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4" />
              Remover
            </button>
          </div>
        </div>

        <SectionTitle>Dados principais</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Nome da escola *" testid="field-name">
            <Input value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="input-name" className={dup ? "border-rose-400 focus-visible:ring-rose-300" : ""} />
            {dup && (
              <div className="mt-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-[12px] text-rose-800" data-testid="school-duplicate-warning">
                <b>⚠️ Essa escola já está cadastrada</b>
                <div className="mt-1.5 rounded-lg bg-white border border-rose-100 p-2 text-slate-700 space-y-0.5" data-testid="school-duplicate-card">
                  <p className="font-black text-slate-900">{dup.name}</p>
                  {dup.city && <p>Cidade: {dup.city}</p>}
                  {dup.cnpj && <p>CNPJ: {dup.cnpj}</p>}
                  {dup.director_name && <p>Diretor(a): {dup.director_name}</p>}
                  {dup.phone && <p>Telefone: {dup.phone}</p>}
                </div>
                <p className="mt-1.5">{dupBlocks ? "Não é possível salvar em duplicidade. Se precisar mudar algo, altere no cadastro existente." : "Este cadastro e o de cima parecem ser a mesma escola."}</p>
                <button type="button" onClick={() => nav(`/escolas/${dup.id}`)} className="mt-1.5 h-9 px-3 rounded-lg bg-rose-600 text-white font-black" data-testid="school-duplicate-open">Abrir e editar o cadastro existente →</button>
              </div>
            )}
          </Field>
          <Field label="Apelido da escola">
            <Input value={form.nickname} onChange={(e) => update("nickname", e.target.value)} data-testid="input-nickname" />
          </Field>
          <Field label="E-mail da escola">
            <Input
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="contato@escola.sp.gov.br"
              data-testid="input-email"
            />
          </Field>
          <Field label="CNPJ">
            <Input
              value={form.cnpj}
              onChange={(e) => update("cnpj", maskCNPJ(e.target.value))}
              placeholder="00.000.000/0000-00"
              inputMode="numeric"
              data-testid="input-cnpj"
            />
          </Field>
          <Field label="Tipo de escola">
            <Select value={form.school_type} onValueChange={(v) => update("school_type", v)}>
              <SelectTrigger data-testid="select-school-type"><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="municipal">Municipal</SelectItem>
                <SelectItem value="estadual">Estadual</SelectItem>
                <SelectItem value="particular">Particular</SelectItem>
                <SelectItem value="federal">Federal</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Data de cadastro">
            <Input type="date" value={form.registration_date} onChange={(e) => update("registration_date", e.target.value)} data-testid="input-registration-date" />
          </Field>
        </div>

        <SectionTitle>Endereço</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="CEP (preenche o endereço automaticamente)">
            <div className="flex gap-2">
              <Input
                value={form.cep}
                onChange={(e) => {
                  const v = maskCEP(e.target.value);
                  update("cep", v);
                  if (v.replace(/\D/g, "").length === 8) handleCepLookup(v);
                }}
                placeholder="00000-000"
                inputMode="numeric"
                data-testid="input-cep"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => handleCepLookup()}
                disabled={cepLoading}
                data-testid="btn-buscar-cep"
                className="shrink-0"
              >
                <Search className="w-4 h-4 mr-1" />
                {cepLoading ? "Buscando..." : "Buscar"}
              </Button>
            </div>
          </Field>
          <Field label="Endereço (rua)">
            <Input value={form.address} onChange={(e) => update("address", e.target.value)} data-testid="input-address" />
          </Field>
          <Field label="Número">
            <Input
              value={form.address_number}
              onChange={(e) => update("address_number", e.target.value)}
              placeholder="123"
              data-testid="input-address-number"
            />
          </Field>
          <Field label="Bairro">
            <Input value={form.neighborhood} onChange={(e) => update("neighborhood", e.target.value)} data-testid="input-neighborhood" />
          </Field>
          <Field label="Cidade">
            <Input value={form.city} onChange={(e) => update("city", e.target.value)} data-testid="input-city" />
          </Field>
          <Field label="UF">
            <Input value={form.state} onChange={(e) => update("state", e.target.value.toUpperCase().slice(0, 2))} maxLength={2} placeholder="SP" data-testid="input-state" />
          </Field>
        </div>

        <SectionTitle>Contatos</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Telefone fixo">
            <Input
              value={form.phone}
              onChange={(e) => update("phone", maskPhone(e.target.value))}
              placeholder="(11) 0000-0000"
              inputMode="tel"
              data-testid="input-phone"
            />
          </Field>
          <Field label="WhatsApp do diretor">
            <Input
              value={form.whatsapp_director}
              onChange={(e) => update("whatsapp_director", maskPhone(e.target.value))}
              placeholder="(11) 90000-0000"
              inputMode="tel"
              data-testid="input-whatsapp"
            />
          </Field>
          <Field label="Nome do diretor">
            <Input value={form.director_name} onChange={(e) => update("director_name", e.target.value)} data-testid="input-director-name" />
          </Field>
          <Field label="E-mail de acesso ao sistema">
            <div className="relative">
              <Input
                value={form.system_email}
                disabled
                readOnly
                placeholder="(preenchido quando o diretor cadastrar o acesso)"
                className="bg-slate-50 pr-9 text-slate-600"
                data-testid="input-system-email"
              />
              <Lock className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </Field>
          {form.registered_user_name && (
            <Field label="Nome usado no cadastro do acesso">
              <Input value={form.registered_user_name} disabled readOnly className="bg-slate-50 text-slate-600" data-testid="input-registered-name" />
            </Field>
          )}
        </div>

        <SectionTitle>Termo de colaboração</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Termo de colaboração">
            <Input value={form.collaboration_term} onChange={(e) => update("collaboration_term", e.target.value)} data-testid="input-term" />
          </Field>
          <Field label="Código atual do termo">
            <Input value={form.current_term_code} onChange={(e) => update("current_term_code", e.target.value)} data-testid="input-term-current" />
          </Field>
          <Field label="Novo código do termo">
            <Input value={form.new_term_code} onChange={(e) => update("new_term_code", e.target.value)} data-testid="input-term-new" />
          </Field>
        </div>

        <SectionTitle>Situação</SectionTitle>
        <div className="flex items-center gap-3">
          <Switch checked={form.active} onCheckedChange={(v) => update("active", v)} data-testid="switch-active" />
          <span className="text-sm text-slate-700">{form.active ? "Escola ativa" : "Escola inativa"}</span>
        </div>

        <SectionTitle>Faz Depois Paga (FDP)</SectionTitle>
        <div className="flex items-center gap-3 mb-3">
          <Switch checked={!!form.fdp_active} onCheckedChange={(v) => update("fdp_active", v)} data-testid="switch-fdp-active" />
          <span className="text-sm text-slate-700">
            {form.fdp_active ? "Escola ativa no FDP" : "FDP desativado para esta escola"}
          </span>
        </div>
        {form.fdp_active && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Valor máximo FDP (R$)">
              <MoneyInput
                value={form.fdp_max_value}
                onChange={(v) => update("fdp_max_value", v)}
                data-testid="input-fdp-max"
              />
            </Field>
            <Field label="Período (meses)">
              <Input
                type="number"
                min="1"
                value={form.fdp_period_months}
                onChange={(e) => update("fdp_period_months", Number(e.target.value) || 0)}
                data-testid="input-fdp-period"
              />
            </Field>
            <Field label="Início">
              <Input
                type="date"
                value={form.fdp_start_date || ""}
                onChange={(e) => update("fdp_start_date", e.target.value)}
                data-testid="input-fdp-start"
              />
            </Field>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 sticky bottom-4 bg-white/80 backdrop-blur p-3 rounded-xl border border-slate-200">
        <Button variant="outline" onClick={() => nav("/escolas")} data-testid="btn-cancelar">
          Cancelar
        </Button>
        <Button onClick={save} disabled={saving} className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white" data-testid="btn-salvar-escola">
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Salvando..." : "Salvar"}
        </Button>
      </div>
    </div>
  );
}

const SectionTitle = ({ children }) => (
  <h3 className="font-heading text-sm font-semibold text-slate-900 border-b border-slate-100 pb-2">{children}</h3>
);

const Field = ({ label, children, testid }) => (
  <div className="space-y-1.5" data-testid={testid}>
    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
    {children}
  </div>
);

const PhotoPreview = ({ path }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let objUrl;
    (async () => {
      try {
        const r = await api.get(`/files/${path}`, { responseType: "blob" });
        objUrl = URL.createObjectURL(r.data);
        setUrl(objUrl);
      } catch (e) {}
    })();
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [path]);
  if (!url) return <Building2 className="w-10 h-10 text-slate-300" />;
  return <img src={url} alt="Escola" className="w-full h-full object-cover" />;
};
